import { useMemo } from 'react';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  listPortfolios,
  createPortfolio,
  deletePortfolio,
  patchPortfolio,
  type Portfolio,
  type PortfolioConfig,
} from '../api/portfoliosApi';

type PortfolioSnapshot = {
  userId: string;
  portfolioId: string;
  cashBalance: number;
  deployedCapital: number;
  totalEquity: number;
  unrealizedPnl: number;
  realizedPnl: number;
  openPositionCount: number;
  totalTradesCount: number;
  winCount: number;
  lossCount: number;
  winRate: number;
  profitFactor: number;
  maxDrawdownPct: number;
  positions: Array<Record<string, unknown>>;
};

type State = {
  portfolios: Portfolio[];
  /** Per-portfolio live snapshot pushed via `portfolio-update` WS messages. */
  snapshotsByPortfolio: Record<string, PortfolioSnapshot>;
  selectedPaperPortfolioId: string | null;
  selectedLivePortfolioId: string | null;
  loading: boolean;
  error: string | null;
  lastFetchedAt: number | null;
};

type Actions = {
  refresh: () => Promise<void>;
  create: (args: {
    name: string;
    startingBalance: number;
    strategies?: string[];
    config?: Partial<PortfolioConfig>;
  }) => Promise<Portfolio>;
  remove: (id: string) => Promise<void>;
  rename: (id: string, name: string) => Promise<void>;
  setStrategies: (id: string, strategies: string[]) => Promise<void>;
  /** Patch the wallet's trading config (risk, caps, per-strategy weights). */
  updateConfig: (id: string, config: Partial<PortfolioConfig>) => Promise<void>;
  /** Convenience: add `strategyId` to the portfolio's enabled list if not present. */
  attachStrategy: (portfolioId: string, strategyId: string) => Promise<void>;
  /** Convenience: remove `strategyId` from the portfolio's enabled list. */
  detachStrategy: (portfolioId: string, strategyId: string) => Promise<void>;
  selectPaper: (id: string | null) => void;
  selectLive: (id: string | null) => void;
  applySnapshot: (snapshot: PortfolioSnapshot) => void;
  reset: () => void;
};

const INITIAL: State = {
  portfolios: [],
  snapshotsByPortfolio: {},
  selectedPaperPortfolioId: null,
  selectedLivePortfolioId: null,
  loading: false,
  error: null,
  lastFetchedAt: null,
};

export const usePortfoliosStore = create<State & Actions>()(
  persist(
    (set, get) => ({
      ...INITIAL,

      async refresh() {
        set({ loading: true, error: null });
        try {
          const { portfolios } = await listPortfolios();
          // Auto-select a paper portfolio if none is selected and one exists.
          const currentPaperId = get().selectedPaperPortfolioId;
          const stillHasCurrentPaper = currentPaperId
            ? portfolios.some((p) => p._id === currentPaperId && p.type === 'paper' && p.isActive)
            : false;
          const nextPaperId = stillHasCurrentPaper
            ? currentPaperId
            : (portfolios.find((p) => p.type === 'paper' && p.isActive)?._id ?? null);
          // Mirror for LIVE wallets: keep the current selection if still valid,
          // else auto-select the first active live wallet (null if none). This
          // lets surfaces default to the live wallet when one exists.
          const currentLiveId = get().selectedLivePortfolioId;
          const stillHasCurrentLive = currentLiveId
            ? portfolios.some((p) => p._id === currentLiveId && p.type === 'live' && p.isActive)
            : false;
          const nextLiveId = stillHasCurrentLive
            ? currentLiveId
            : (portfolios.find((p) => p.type === 'live' && p.isActive)?._id ?? null);
          set({
            portfolios,
            loading: false,
            lastFetchedAt: Date.now(),
            selectedPaperPortfolioId: nextPaperId,
            selectedLivePortfolioId: nextLiveId,
          });
        } catch (err) {
          set({ loading: false, error: (err as Error).message });
        }
      },

      async create(args) {
        const { portfolio } = await createPortfolio(args);
        set((s) => ({
          portfolios: [portfolio, ...s.portfolios],
          selectedPaperPortfolioId:
            portfolio.type === 'paper' ? portfolio._id : s.selectedPaperPortfolioId,
        }));
        return portfolio;
      },

      async remove(id) {
        await deletePortfolio(id);
        set((s) => {
          const next = s.portfolios.filter((p) => p._id !== id);
          const wasPaperSelected = s.selectedPaperPortfolioId === id;
          const wasLiveSelected = s.selectedLivePortfolioId === id;
          return {
            portfolios: next,
            selectedPaperPortfolioId: wasPaperSelected
              ? (next.find((p) => p.type === 'paper' && p.isActive)?._id ?? null)
              : s.selectedPaperPortfolioId,
            selectedLivePortfolioId: wasLiveSelected ? null : s.selectedLivePortfolioId,
          };
        });
      },

      async rename(id, name) {
        const { portfolio } = await patchPortfolio(id, { name });
        set((s) => ({
          portfolios: s.portfolios.map((p) => (p._id === id ? portfolio : p)),
        }));
      },

      async setStrategies(id, strategies) {
        const { portfolio } = await patchPortfolio(id, { strategies });
        set((s) => ({
          portfolios: s.portfolios.map((p) => (p._id === id ? portfolio : p)),
        }));
      },

      async updateConfig(id, config) {
        const { portfolio } = await patchPortfolio(id, { config });
        set((s) => ({
          portfolios: s.portfolios.map((p) => (p._id === id ? portfolio : p)),
        }));
      },

      async attachStrategy(portfolioId, strategyId) {
        const current = get().portfolios.find((p) => p._id === portfolioId);
        if (!current) return;
        if (current.strategies.includes(strategyId)) return;
        const next = [...current.strategies, strategyId];
        const { portfolio } = await patchPortfolio(portfolioId, { strategies: next });
        set((s) => ({
          portfolios: s.portfolios.map((p) =>
            p._id === portfolioId ? portfolio : p,
          ),
        }));
      },

      async detachStrategy(portfolioId, strategyId) {
        const current = get().portfolios.find((p) => p._id === portfolioId);
        if (!current) return;
        if (!current.strategies.includes(strategyId)) return;
        const next = current.strategies.filter((s) => s !== strategyId);
        const { portfolio } = await patchPortfolio(portfolioId, { strategies: next });
        set((s) => ({
          portfolios: s.portfolios.map((p) =>
            p._id === portfolioId ? portfolio : p,
          ),
        }));
      },

      selectPaper(id) {
        set({ selectedPaperPortfolioId: id });
      },

      selectLive(id) {
        set({ selectedLivePortfolioId: id });
      },

      applySnapshot(snapshot) {
        set((s) => ({
          snapshotsByPortfolio: {
            ...s.snapshotsByPortfolio,
            [snapshot.portfolioId]: snapshot,
          },
        }));
      },

      reset() {
        set(INITIAL);
      },
    }),
    {
      name: 'lynx.portfolios.v1',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        selectedPaperPortfolioId: s.selectedPaperPortfolioId,
        selectedLivePortfolioId: s.selectedLivePortfolioId,
      }),
    },
  ),
);

// ── Stable selectors ─────────────────────────────────────────
const EMPTY_PORTFOLIOS: Portfolio[] = [];

export const selectPaperPortfolios = (s: State): Portfolio[] =>
  s.portfolios.filter((p) => p.type === 'paper' && p.isActive);

export const selectLivePortfolios = (s: State): Portfolio[] =>
  s.portfolios.filter((p) => p.type === 'live' && p.isActive);

export const selectSelectedPaperPortfolio = (s: State): Portfolio | null => {
  if (!s.selectedPaperPortfolioId) return null;
  return s.portfolios.find((p) => p._id === s.selectedPaperPortfolioId) ?? null;
};

export const selectSelectedPaperSnapshot = (s: State): PortfolioSnapshot | null => {
  if (!s.selectedPaperPortfolioId) return null;
  return s.snapshotsByPortfolio[s.selectedPaperPortfolioId] ?? null;
};

/**
 * An open position flattened out of a portfolio snapshot, tagged with the
 * owning portfolio's type ('paper' | 'live') and name. Chart overlays use the
 * type to colour paper-trade lines differently from live (capital) ones.
 */
export type TaggedPosition = {
  symbol: string;
  side: string;
  entryPrice: number;
  currentPrice: number;
  stopLossPrice?: number | undefined;
  takeProfitPrice?: number | undefined;
  unrealizedPnl?: number | undefined;
  positionQty?: number | undefined;
  positionSize?: number | undefined;
  openedAt?: number | undefined;
  tradeId?: string | undefined;
  strategyId?: string | undefined;
  /** Cross-sectional basket hold — exits by rebalance rotation, not its SL/TP. */
  isBasket?: boolean | undefined;
  /** Rotation proximity (basket only; as-of-last-rebalance). */
  rotationRank?: number | undefined;
  rotationTopK?: number | undefined;
  rotationRegimeClosed?: boolean | undefined;
  onBubble?: boolean | undefined;
  nextRebalanceTs?: number | undefined;
  portfolioId: string;
  portfolioType: 'paper' | 'live';
  portfolioName: string;
  /** Any other engine-published fields ride along untyped. */
  [key: string]: unknown;
};

const EMPTY_TAGGED: TaggedPosition[] = [];

/**
 * Flatten every open position across active portfolios, each tagged with its
 * portfolio's type + name. Built from the live snapshots (source of truth for
 * positions) joined to the portfolio list (source of truth for type).
 *
 * This is a PLAIN function, NOT a zustand selector: it allocates a new array +
 * new objects every call, which would make `useStore(selectTaggedOpenPositions)`
 * loop forever (React's useSyncExternalStore sees a fresh snapshot each render).
 * Callers must instead subscribe to the two stable raw slices
 * (`portfolios` + `snapshotsByPortfolio`) and wrap this in a `useMemo` — see
 * `useTaggedOpenPositions`.
 */
export function buildTaggedOpenPositions(
  portfolios: Portfolio[],
  snapshotsByPortfolio: Record<string, PortfolioSnapshot>,
): TaggedPosition[] {
  const byId = new Map(portfolios.map((p) => [p._id, p]));
  const out: TaggedPosition[] = [];
  for (const [portfolioId, snap] of Object.entries(snapshotsByPortfolio)) {
    const meta = byId.get(portfolioId);
    // Skip snapshots whose portfolio is gone or deactivated.
    if (!meta || !meta.isActive) continue;
    for (const pos of snap.positions) {
      const symbol = pos.symbol;
      if (typeof symbol !== 'string') continue;
      const num = (k: string): number | undefined =>
        typeof pos[k] === 'number' ? (pos[k] as number) : undefined;
      const str = (k: string): string | undefined =>
        typeof pos[k] === 'string' ? (pos[k] as string) : undefined;
      out.push({
        ...pos,
        symbol,
        side: str('side') ?? '',
        entryPrice: num('entryPrice') ?? 0,
        currentPrice: num('currentPrice') ?? 0,
        stopLossPrice: num('stopLossPrice'),
        takeProfitPrice: num('takeProfitPrice'),
        unrealizedPnl: num('unrealizedPnl'),
        positionQty: num('positionQty'),
        positionSize: num('positionSize'),
        openedAt: num('openedAt'),
        tradeId: str('tradeId'),
        strategyId: str('strategyId'),
        isBasket: pos['isBasket'] === true,
        portfolioId,
        portfolioType: meta.type,
        portfolioName: meta.name,
      });
    }
  }
  return out.length === 0 ? EMPTY_TAGGED : out;
}

/**
 * Hook returning all tagged open positions, memoized on the two raw store
 * slices so the result reference is stable until positions actually change.
 * This is the safe way to consume `buildTaggedOpenPositions` from a component.
 */
export function useTaggedOpenPositions(): TaggedPosition[] {
  const portfolios = usePortfoliosStore((s) => s.portfolios);
  const snapshots = usePortfoliosStore((s) => s.snapshotsByPortfolio);
  return useMemo(
    () => buildTaggedOpenPositions(portfolios, snapshots),
    [portfolios, snapshots],
  );
}

// Silence unused export warning on the empty fallback.
export { EMPTY_PORTFOLIOS };
